/**
 * Utilities for extracting and working with image validation metadata
 */

import { API_IMAGE_TYPE_MAP, type ImageAsset, type ImageType } from "../types/image-validation.types";

/**
 * Extract validation warnings from image assets array
 * @returns Map of image type to warnings array
 */
export function extractValidationWarnings(imageAssets: ImageAsset[] | undefined): Partial<Record<ImageType, string[]>> {
  const warnings: Partial<Record<ImageType, string[]>> = {};
  if (!imageAssets) return warnings;

  // Type assertion for the map - ensure it's treated as Record<string, ImageType>
  const typeMap = API_IMAGE_TYPE_MAP as unknown as Record<string, ImageType>;

  for (const asset of imageAssets) {
    const imageType = typeMap[asset.imageType];
    if (!imageType || !asset.metadata) continue;

    const validation = asset.metadata.validation;
    if (validation?.warnings && validation.warnings.length > 0) {
      warnings[imageType] = validation.warnings;
    }
  }

  return warnings;
}

/**
 * Extract original filenames from image assets array
 * @returns Map of image type to original filename
 */
export function extractOriginalFilenames(imageAssets: ImageAsset[] | undefined): Partial<Record<ImageType, string>> {
  const filenames: Partial<Record<ImageType, string>> = {};
  if (!imageAssets) return filenames;

  const typeMap = API_IMAGE_TYPE_MAP as unknown as Record<string, ImageType>;

  for (const asset of imageAssets) {
    const imageType = typeMap[asset.imageType];
    if (imageType) {
      filenames[imageType] = asset.originalFileName;
    }
  }

  return filenames;
}

/**
 * Check if an image type has validation warnings
 */
export function hasValidationWarnings(imageType: ImageType, warnings: Partial<Record<ImageType, string[]>>): boolean {
  const typeWarnings = warnings[imageType];
  return Boolean(typeWarnings && typeWarnings.length > 0);
}

/**
 * Get validation warnings for a specific image type
 */
export function getValidationWarnings(imageType: ImageType, warnings: Partial<Record<ImageType, string[]>>): string[] {
  return warnings[imageType] || [];
}
